import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FaBoxOpen,
  FaCalendarAlt,
  FaFileInvoice,
  FaTimesCircle,
  FaUndoAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function OrderHistory() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const parentId = localStorage.getItem("parentId");
  const BASE_URL =
    process.env.REACT_APP_BACKEND_URL || "https://uniformnx-com.onrender.com";

  useEffect(() => {
    if (parentId) {
      fetchOrders();
    } else {
      setLoading(false);
      setError("User not logged in");
    }
  }, [parentId]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/orders/parent/${parentId}`);
      setOrders(res.data);
    } catch (err) {
      console.error("Order fetch failed:", err);
      setError(`Failed to fetch: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (orderId) => {
    const result = await Swal.fire({
      title: "Cancel Order?",
      text: "Are you sure you want to cancel this order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, Cancel it!",
    });

    if (result.isConfirmed) {
      try {
        await axios.put(`${BASE_URL}/orders/cancel/${orderId}`);
        Swal.fire("Cancelled!", "Your order has been cancelled.", "success");
        fetchOrders();
      } catch (err) {
        Swal.fire(
          "Error",
          err.response?.data?.message || "Could not cancel the order",
          "error"
        );
      }
    }
  };

  const handleReturn = async (orderId) => {
    const { value: reason } = await Swal.fire({
      title: "Return Request",
      input: "textarea",
      inputLabel: "Reason for return",
      inputPlaceholder: "E.g. Size issues, damaged product...",
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) return "Please enter a reason!";
      },
    });

    if (reason) {
      try {
        await axios.put(`${BASE_URL}/orders/return/${orderId}`, {
          reason,
        });
        Swal.fire("Submitted!", "Your return request has been submitted.", "success");
        fetchOrders();
      } catch (err) {
        Swal.fire("Error", "Could not submit the return request", "error");
      }
    }
  };

  const handleInvoice = (order) => {
    const printWindow = window.open("", "_blank");

    const customerName = order.address?.name || order.parentName || "Valued Customer";
    const customerPhone = order.address?.phone || "N/A";
    const totalAmount = order.totalAmount || 0;
    const taxableAmount = totalAmount;
    const netAmount = totalAmount;

    const numberToWords = (num) => {
      const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
      const tens = ["", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
      const twenties = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

      if (num === 0) return "Zero";

      const numStr = Math.floor(num).toString();
      let words = "";

      if (numStr.length > 2) {
        words += ones[numStr[0]] + " Hundred ";
        if (numStr.substring(1) !== "00") {
          words += "and ";
        }
      }

      const lastTwo = parseInt(numStr.substring(-2));
      if (lastTwo > 0) {
        if (lastTwo < 20) {
          words += tens[lastTwo];
        } else {
          words += twenties[Math.floor(lastTwo / 10)];
          if (lastTwo % 10 > 0) {
            words += " " + ones[lastTwo % 10];
          }
        }
      }

      return words + " Only";
    };

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice #${order._id.slice(-8)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #000; line-height: 1.4; margin: 0; }
            .invoice-header { text-align: center; margin-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
            .company-subtitle { font-size: 14px; color: #666; }
            .invoice-title { font-size: 20px; font-weight: bold; margin: 20px 0 10px 0; text-align: center; }
            .invoice-details { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .detail-col { width: 48%; }
            .detail-row { display: flex; margin-bottom: 5px; }
            .detail-label { width: 120px; font-weight: bold; font-size: 12px; }
            .detail-value { flex: 1; font-size: 12px; }
            .address-section { display: flex; justify-content: space-between; margin-bottom: 20px; }
            .address-col { width: 48%; border: 1px solid #ccc; padding: 10px; }
            .address-title { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
            .address-content { font-size: 12px; line-height: 1.3; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 12px; }
            th { border: 1px solid #000; padding: 5px; text-align: center; background: #f5f5f5; font-weight: bold; }
            td { border: 1px solid #000; padding: 5px; text-align: center; }
            .text-left { text-align: left; }
            .text-right { text-align: right; }
            .bank-details { margin-top: 20px; }
            .bank-title { font-weight: bold; margin-bottom: 10px; font-size: 14px; }
            .bank-row { display: flex; margin-bottom: 5px; font-size: 12px; }
            .bank-label { width: 80px; font-weight: bold; }
            .bank-value { flex: 1; }
            .summary-section { margin-top: 20px; }
            .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 12px; }
            .summary-label { font-weight: bold; }
            .summary-value { text-align: right; }
            .amount-words { margin-top: 10px; font-size: 12px; font-style: italic; }
            .signatures { display: flex; justify-content: space-between; margin-top: 40px; }
            .signature-box { width: 45%; text-align: center; }
            .signature-line { border-bottom: 1px solid #000; margin-bottom: 5px; height: 40px; }
            .signature-text { font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <div class="company-name">SHREENATH NX</div>
            <div class="company-subtitle">GST: 24AAUPP5605D1ZV | PAN: AAUPP5605D | MOB: 99244 63040</div>
          </div>

          <div class="invoice-title">TAX INVOICE</div>

          <div class="invoice-details">
            <div class="detail-col">
              <div class="detail-row">
                <span class="detail-label">Invoice No.:</span>
                <span class="detail-value">WT/0001</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Invoice Date:</span>
                <span class="detail-value">${new Date(order.createdAt).toLocaleDateString("en-IN")}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Date Of Supply:</span>
                <span class="detail-value">${new Date(order.createdAt).toLocaleDateString("en-IN")}</span>
              </div>
            </div>
            <div class="detail-col">
              <div class="detail-row">
                <span class="detail-label">Place Of Supply:</span>
                <span class="detail-value">GUJARAT</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Order No.:</span>
                <span class="detail-value">#${order._id.slice(-8).toUpperCase()}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Dispatched No:</span>
                <span class="detail-value">N/A</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">SalesMan:</span>
                <span class="detail-value">N/A</span>
              </div>
            </div>
          </div>

          <div class="address-section">
            <div class="address-col">
              <div class="address-title">Bill To</div>
              <div class="address-content">
                <div><strong>${customerName}</strong></div>
                <div>${order.address?.addressLine || "N/A"}</div>
                <div>${order.address?.city || ""}, ${order.address?.pincode || ""}</div>
                <div>Phone: ${customerPhone}</div>
                <div>GST No.: N/A</div>
                <div>State: GUJARAT</div>
                <div>Code: 24</div>
              </div>
            </div>
            <div class="address-col">
              <div class="address-title">Ship To</div>
              <div class="address-content">
                <div><strong>${customerName}</strong></div>
                <div>${order.address?.addressLine || "N/A"}</div>
                <div>${order.address?.city || ""}, ${order.address?.pincode || ""}</div>
                <div>Phone: ${customerPhone}</div>
                <div>GST No.: N/A</div>
                <div>State: GUJARAT</div>
                <div>Code: 24</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Particulars</th>
                <th>HSN</th>
                <th>Size</th>
                <th>Qty</th>
                <th>UOM</th>
                <th>Rate</th>
                <th>Discount</th>
                <th>Net Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map((i) => `
                <tr>
                  <td class="text-left">${i.name || i.description || "Uniform Item"}</td>
                  <td>6109</td>
                  <td>${i.size || "N/A"}</td>
                  <td>${i.quantity}</td>
                  <td>NOS</td>
                  <td class="text-right">₹${i.price}</td>
                  <td>0.00</td>
                  <td class="text-right">₹${i.price}</td>
                  <td class="text-right">₹${(i.price * i.quantity).toFixed(2)}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>

          <div class="bank-details">
            <div class="bank-title">Bank Details</div>
            <div class="bank-row">
              <span class="bank-label">Bank Name:</span>
              <span class="bank-value">THE KARAD URBAN CO-OP. BANK LTD.</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">A/C Name:</span>
              <span class="bank-value">SHREENATH NX</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">A/C No.:</span>
              <span class="bank-value">501200010965922</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">Branch:</span>
              <span class="bank-value">KARAD</span>
            </div>
            <div class="bank-row">
              <span class="bank-label">IFSC Code:</span>
              <span class="bank-value">KARB0000001</span>
            </div>
          </div>

          <div class="summary-section">
            <div class="summary-row">
              <span class="summary-label">Taxable Amount:</span>
              <span class="summary-value">₹${taxableAmount.toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Discount Amount:</span>
              <span class="summary-value">₹0.00</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">All Taxes:</span>
              <span class="summary-value">Included</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Additional Amount:</span>
              <span class="summary-value">₹0.00</span>
            </div>
            <div class="summary-row" style="border-top: 1px solid #000; padding-top: 5px; font-weight: bold;">
              <span class="summary-label">Net Amount:</span>
              <span class="summary-value">₹${netAmount.toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Received Amount:</span>
              <span class="summary-value">₹${netAmount.toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Outstanding Amount:</span>
              <span class="summary-value">₹0.00</span>
            </div>
          </div>

          <div class="amount-words">
            Rupees ${numberToWords(totalAmount)}
          </div>

          <div class="signatures">
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-text">Customer Signature</div>
            </div>
            <div class="signature-box">
              <div class="signature-line"></div>
              <div class="signature-text">For SHREENATH NX</div>
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || "";
    if (s.includes("paid") || s.includes("delivered")) return "#10b981";
    if (s.includes("cancel")) return "#ef4444";
    if (s.includes("return")) return "#6366f1";
    return "#f59e0b";
  };

  if (loading)
    return (
      <div className="loader-screen">
        <div className="spinner"></div>
        <p>Fetching your history...</p>
      </div>
    );

  return (
    <div className="order-page-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
        .order-page-wrapper { background: #f8fafc; min-height: 100vh; padding: 60px 20px; font-family: 'Plus Jakarta Sans', sans-serif; }
        .page-title-section { max-width: 900px; margin: 0 auto 40px auto; }
        .page-title-section h2 { font-weight: 800; color: #1e293b; letter-spacing: -1px; }
        .order-container { max-width: 900px; margin: 0 auto; }
        .glass-card { background: white; border-radius: 24px; border: 1px solid rgba(226, 232, 240, 0.8); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.02); transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); margin-bottom: 30px; overflow: hidden; }
        .card-top { padding: 24px; background: #fcfdfe; border-bottom: 1px dashed #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
        .action-btns { display: flex; gap: 10px; padding: 10px 24px; background: #fff; border-top: 1px solid #f1f5f9; }
        .btn-action {
          padding: 8px 16px; border-radius: 12px; font-size: 0.8rem; font-weight: 700;
          border: none; display: flex; align-items: center; gap: 6px; transition: 0.3s;
        }
        .btn-inv { background: #f1f5f9; color: #475569; }
        .btn-can { background: #fee2e2; color: #dc2626; }
        .btn-ret { background: #eef2ff; color: #4f46e5; }
        .btn-action:hover { opacity: 0.8; transform: translateY(-2px); }
        .product-section { padding: 24px; }
        .item-row { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
        .img-wrapper { width: 80px; height: 80px; border-radius: 16px; background: #f1f5f9; padding: 8px; flex-shrink: 0; }
        .img-wrapper img { width: 100%; height: 100%; object-fit: contain; }
        .item-info h6 { font-weight: 700; color: #1e293b; margin-bottom: 4px; font-size: 1rem; }
        .card-bottom { padding: 20px 24px; background: #f8fafc; border-top: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
        .total-val { font-size: 1.2rem; font-weight: 800; color: #1e293b; }
        .loader-screen { height: 80vh; display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 20px; }
        .spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #097969; border-radius: 50%; animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      <div className="page-title-section text-center">
        <h2 className="mb-2">My Orders</h2>
        <p className="text-muted">Manage your school uniform deliveries</p>
      </div>

      <div className="order-container">
        {error && <p className="text-danger text-center">{error}</p>}

        {orders.length === 0 ? (
          <div className="empty-state text-center p-5 bg-white rounded-5 border shadow-sm">
            <FaBoxOpen size={60} className="text-muted mb-3" />
            <h4>No orders found</h4>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order._id} className="glass-card">
              <div className="card-top">
                <div className="order-ref">
                  <span
                    className="fw-bold text-uppercase"
                    style={{ letterSpacing: "1px" }}
                  >
                    #{order._id.slice(-8)}
                  </span>
                </div>

                <div
                  className="status-chip"
                  style={{
                    backgroundColor: `${getStatusColor(order.status)}15`,
                    color: getStatusColor(order.status),
                    padding: "5px 12px",
                    borderRadius: "20px",
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                  }}
                >
                  {order.status}
                </div>
              </div>

              <div className="product-section">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="item-row">
                    <div className="img-wrapper">
                      <img
                        src={
                          item.uniformId?.image
                            ? `${BASE_URL}/uploads/${item.uniformId.image}`
                            : "https://via.placeholder.com/100"
                        }
                        alt="img"
                      />
                    </div>
                    <div className="item-info flex-grow-1">
                      <h6>{item.name}</h6>
                      <div className="small text-muted">
                        Qty: {item.quantity} |{" "}
                        <span style={{ color: "#097969" }}>
                          {order.schoolName}
                        </span>
                      </div>
                    </div>
                    <div className="text-end fw-bold">₹{item.price}</div>
                  </div>
                ))}
              </div>

              <div className="action-btns">
                <button
                  className="btn-action btn-inv"
                  onClick={() => handleInvoice(order)}
                >
                  <FaFileInvoice /> Invoice
                </button>

                <button
                  onClick={() => navigate(`/track-order/${order._id}`)}
                  className="btn-action btn-inv"
                >
                  <i className="fas fa-truck"></i> Track Order
                </button>

                {order.status === "Pending" && (
                  <button
                    className="btn-action btn-can"
                    onClick={() => handleCancel(order._id)}
                  >
                    <FaTimesCircle /> Cancel
                  </button>
                )}

                {order.status === "Delivered" && (
                  <button
                    className="btn-action btn-ret"
                    onClick={() => handleReturn(order._id)}
                  >
                    <FaUndoAlt /> Return
                  </button>
                )}
              </div>

              <div className="card-bottom">
                <div className="date-box small text-muted">
                  <FaCalendarAlt className="me-1" />{" "}
                  {new Date(order.createdAt).toLocaleDateString()}
                </div>
                <div className="text-end">
                  <span className="total-val">₹{order.totalAmount}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default OrderHistory;
